import { CertificateTextField } from '../../../apis/api-client';

// Blank-canvas dimensions used before any background is uploaded. There is
// intentionally NO built-in default background image — the system ships with
// no artwork, so a tenant that hasn't uploaded one edits on a plain canvas and
// issues certificates on a white background. (Any tenant-specific artwork,
// including Dentlogics', is uploaded per-tenant, never baked into defaults.)
export const TEMPLATE_WIDTH = 1920;
export const TEMPLATE_HEIGHT = 1358;

// Available fonts (must match server-side fonts)
export const FONT_OPTIONS = ['Arial', 'Times New Roman', 'Georgia'];

// Default text fields — starting positions on the blank canvas, laid out
// top-to-bottom (student name, course title, CE hours, IDs, date) so an admin
// has a sensible arrangement to drag against their own uploaded background.
// On background upload, positions rescale proportionally to the new image
// dimensions.
export const DEFAULT_FIELDS: CertificateTextField[] = [
  {
    fieldType: 'StudentName',
    x: 540,
    y: 580,
    fontSize: 52,
    fontFamily: 'Arial',
    fontColor: '#000000',
    fontWeight: 'bold',
    textAlign: 'left',
  },
  {
    fieldType: 'CourseTitle',
    x: 540,
    y: 770,
    fontSize: 44,
    fontFamily: 'Arial',
    fontColor: '#333333',
    fontWeight: 'normal',
    textAlign: 'left',
  },
  {
    fieldType: 'CeHours',
    x: 760,
    y: 850,
    fontSize: 28,
    fontFamily: 'Arial',
    fontColor: '#000000',
    fontWeight: 'bold',
    textAlign: 'left',
  },
  {
    fieldType: 'CourseId',
    x: 1400,
    y: 920,
    fontSize: 24,
    fontFamily: 'Arial',
    fontColor: '#000000',
    fontWeight: 'normal',
    textAlign: 'left',
  },
  {
    fieldType: 'AgdSubjectCode',
    x: 1100,
    y: 920,
    fontSize: 24,
    fontFamily: 'Arial',
    fontColor: '#000000',
    fontWeight: 'normal',
    textAlign: 'left',
  },
  {
    fieldType: 'CompletionDate',
    x: 620,
    y: 1090,
    fontSize: 32,
    fontFamily: 'Arial',
    fontColor: '#000000',
    fontWeight: 'normal',
    textAlign: 'left',
  },
];

export const SYSTEM_FIELD_LABELS: Record<string, string> = {
  StudentName: 'Student Name',
  CourseTitle: 'Course Title',
  CeHours: 'CE Hours',
  CourseId: 'Course ID',
  CompletionDate: 'Completion Date',
  AgdSubjectCode: 'AGD Subject Code',
};

// Ordered list of system field types — drives chip rendering and
// enabled-state initialization. Custom-text fields are not enumerated
// here; they're discovered from `template.textFields` at render time.
export const SYSTEM_FIELD_TYPES = ['StudentName', 'CourseTitle', 'CeHours', 'CourseId', 'AgdSubjectCode', 'CompletionDate'] as const;

// Custom-text fields use a unique `fieldType` prefix so the server can
// tell them apart from system substitution fields and just render their
// `content` verbatim.
export const CUSTOM_TEXT_PREFIX = 'CustomText:';
export const isCustomText = (fieldType: string) => fieldType.startsWith(CUSTOM_TEXT_PREFIX);
export const newCustomTextId = () =>
  `${CUSTOM_TEXT_PREFIX}${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;

// Chip label for any field — system label or truncated custom content.
export function labelForField(field: CertificateTextField): string {
  if (isCustomText(field.fieldType)) {
    const text = (field.content || '').trim();
    if (!text) return 'Custom text';
    return text.length > 18 ? `${text.slice(0, 18)}…` : text;
  }
  return SYSTEM_FIELD_LABELS[field.fieldType] || field.fieldType;
}
